import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";
import { EVENEMENT_COMPTEURS } from "../services/compteurs";
import { forwardNewNotificationsToSystem, prepareSystemNotifications } from "../utils/systemNotifications";
import { signalerNotificationSysteme, suivreNotificationsNonLues } from "../services/sonAlerte";

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)    return "À l'instant";
  if (diff < 3600)  return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
  return `Il y a ${Math.floor(diff / 86400)} j`;
}

export default function NotifBell({ variant = "student" }) {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open,   setOpen]   = useState(false);
  const ref                 = useRef(null);

  const fetchNotifs = useCallback(() => {
    api.get("notifications/").then(r => {
      const notifications = Array.isArray(r.data.notifications) ? r.data.notifications : [];
      setNotifs(notifications);
      setUnread(r.data.unread_count);
      suivreNotificationsNonLues(r.data.unread_count);
      forwardNewNotificationsToSystem(notifications).catch(() => {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    prepareSystemNotifications().then(signalerNotificationSysteme).catch(() => {});
    fetchNotifs();
    const id = setInterval(fetchNotifs, 30000);
    // Une rubrique ouverte a marqué des notifications comme lues : la cloche suit.
    window.addEventListener(EVENEMENT_COMPTEURS, fetchNotifs);
    return () => { clearInterval(id); window.removeEventListener(EVENEMENT_COMPTEURS, fetchNotifs); };
  }, [fetchNotifs]);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAll = async () => {
    await api.patch("notifications/", {});
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);
  };

  const markOne = async (id) => {
    await api.patch("notifications/", { id });
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const handleOpen = () => {
    prepareSystemNotifications().catch(() => {});
    setOpen(v => !v);
    if (!open && unread > 0) markAll();
  };

  // Styles adaptés selon le contexte (student = icône blanche, admin = icône sombre)
  const btnClass = variant === "admin" ? "ap-topbar-icon-btn ql-notif-bell-admin" : "ql-notif";

  return (
    <div className="ql-notif-wrap" ref={ref} style={{ position: "relative" }}>
      <div className={btnClass} onClick={handleOpen} style={{ cursor: "pointer" }} title="Notifications">
        <i className="bi bi-bell"></i>
        {unread > 0 && <span className="ql-notif-dot">{unread > 9 ? "9+" : unread}</span>}
      </div>

      {open && (
        <div className="ql-notif-dropdown">
          <div className="ql-notif-header">
            <span className="fw-bold">Notifications</span>
            {unread > 0 && (
              <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.78rem" }} onClick={markAll}>
                Tout marquer comme lu
              </button>
            )}
          </div>
          <div className="ql-notif-list">
            {notifs.length === 0 ? (
              <div className="ql-notif-empty">Aucune notification</div>
            ) : notifs.map(n => (
              <div
                key={n.id}
                className={`ql-notif-item${n.is_read ? "" : " ql-notif-item--unread"}`}
                onClick={() => { if (!n.is_read) markOne(n.id); }}
              >
                <div className="ql-notif-item-title">{n.title}</div>
                <div className="ql-notif-item-content">{n.content}</div>
                <div className="ql-notif-item-time">{timeAgo(n.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
